export const API_CONFIG = {
  auth: {
    login: '/v1/customer/auth/login',
    signup: '/v1/customer/auth/signup',
    complete: '/v1/customer/auth/complete',
    forgotPassword: '/v1/customer/auth/forgot-password',
    resetPassword: '/v1/customer/auth/reset-password',
    setPassword: '/v1/customer/auth/set-password',
    verifyOtp: '/v1/auth/email-otp/verify',
    requestOtp: '/v1/customer/auth/otp/request',
    loginViaOtp: '/v1/customer/auth/login-via-otp',
    logout: '/v1/customer/auth/logout',
    refresh: '/v1/auth/refresh',
    workspaces: '/v1/customer/auth/workspaces',
    switchWorkspace: '/v1/customer/auth/switch-workspace',
  },
  shortlist: {
    list: '/v1/customer/shortlist',
    add: '/v1/customer/shortlist',
    remove: (projectId: string) => `/v1/customer/shortlist/${projectId}`,
    check: (projectId: string) => `/v1/customer/shortlist/check/${projectId}`,
  },
  home: '/v1/customer/home',
  customerProjects: {
    search: '/v1/customer/projects/search',
    detail: (id: string) => `/v1/customer/projects/${id}`,
    list: '/v1/customer/projects/list',
    towers: (projectId: string) => `/v1/projects/${projectId}/towers`,
    towerUnitDetails: (projectId: string, towerId: string) =>
      `/v1/projects/${projectId}/towers/${towerId}/unit-details`,
  },
  profile: {
    get: '/v1/customer/profile',
    update: '/v1/customer/profile',
    changePassword: '/v1/customer/profile/change-password',
  },
  dashboard: {
    get: '/v1/customer/dashboard',
  },
  builders: {
    list: '/v1/customer/builders',
  },
  /** Customer portal quotations — scoped via auth JWT (no `customerId` query param). */
  customerQuotations: {
    list: '/v1/customer/quotations',
    get: (id: string) => `/v1/customer/quotations/${id}`,
    history: (id: string) => `/v1/customer/quotations/${id}/history`,
    recordView: (id: string) => `/v1/customer/quotations/${id}/view`,
    updateStatus: (id: string) => `/v1/customer/quotations/${id}/status`,
  },
  /** Customer opportunities + quotations (scoped via auth JWT). */
  opportunities: {
    list: '/v1/opportunities',
    quotations: (opportunityId: string) => `/v1/opportunities/${opportunityId}/quotations`,
    quotation: (opportunityId: string, quotationId: string) =>
      `/v1/opportunities/${opportunityId}/quotations/${quotationId}`,
    updateStatus: (opportunityId: string, quotationId: string) =>
      `/v1/opportunities/${opportunityId}/quotations/${quotationId}/status`,
  },
  /** CRM calendar events (site visits / meetings) scoped via customer opportunities. */
  crmCalendarEvents: {
    byOpportunity: (opportunityId: string) =>
      `/v1/crm-calendar-events/by-opportunity/${opportunityId}`,
    byLead: (leadId: string) => `/v1/crm-calendar-events/by-lead/${leadId}`,
    byId: (id: string) => `/v1/crm-calendar-events/${id}`,
    myEvents: '/v1/crm-calendar-events/my-events',
    update: (eventId: string) => `/v1/crm-calendar-events/${eventId}`,
  },
  /** Customer meetings (site visits) — list and update via auth JWT. */
  meetings: {
    list: '/v1/customer/meetings',
    create: '/v1/customer/meetings',
    update: (id: string) => `/v1/customer/meetings/${id}`,
  },
  customerQuotationRequest: {
    request: '/v1/customer/quotations/request',
  },
  siteVisits: {
    request: '/v1/customer/site-visits/request',
    reschedule: (siteVisitId: string) => `/v1/customer/site-visits/${siteVisitId}/reschedule`,
  },

  /** Customer work progress (`/v1/customer/work-progress`). */
  workProgress: {
    list: '/v1/customer/work-progress',
    detail: (towerId: string) => `/v1/customer/work-progress/${towerId}`,
    stageImages: (towerId: string, stageId: string) =>
      `/v1/customer/work-progress/${towerId}/stages/${stageId}/images`,
  },
  /** Builder project APIs (`/v1/projects`) used for quotation project context. */
  projects: {
    draft: (projectId: string) => `/v1/projects/draft/${projectId}`,
    amenities: (projectId: string) => `/v1/projects/${projectId}/amenities`,
    paymentPlans: (projectId: string, towerId: string) =>
      `/v1/projects/${projectId}/towers/${towerId}/payment-plans`,
  },
  developerDetails: {
    byId: (developerId: string) => `/v1/developer-details/${developerId}`,
  },
  files: {
    previewUrl: '/v1/files/preview-url',
    publicUrl: '/v1/files/public-url',
    stream: '/v1/files/stream',
  },
  /** Builder demand letters & payments (`/v1/booking/demand-payments`). */
  demandPayments: {
    list: '/v1/booking/demand-payments',
    ownerDetails: (bookingId: string) =>
      `/v1/booking/demand-payments/bookings/${bookingId}/owner-details`,
    overview: (demandLetterId: string) =>
      `/v1/booking/demand-payments/${demandLetterId}/overview`,
    demandLetters: (bookingId: string) =>
      `/v1/booking/demand-payments/${bookingId}/demand-letters`,
    payments: (bookingId: string) => `/v1/booking/demand-payments/${bookingId}/payments`,
    manualPayment: (demandLetterId: string) =>
      `/v1/booking/demand-payments/${demandLetterId}/manual-payment`,
    downloadNotice: (demandLetterId: string) =>
      `/v1/booking/demand-payments/${demandLetterId}/download-notice`,
    receipt: (receiptId: string) => `/v1/booking/demand-payments/receipts/${receiptId}`,
    downloadReceipt: (receiptId: string) =>
      `/v1/receipts/${receiptId}/download`,

  },
  /** Customer portal bookings list + owner/co-owner workflow (scoped via customer auth JWT). */
  customerBookings: {
    list: '/v1/customer/bookings',
    uploadDocument: (bookingId: string) =>
      `/v1/customer/bookings/${bookingId}/owner-workflow/documents`,
    deleteDocument: (bookingId: string, documentId: string) =>
      `/v1/customer/bookings/${bookingId}/owner-workflow/documents/${documentId}`,
    documentGeneration: (bookingId: string) =>
      `/v1/customer/bookings/${bookingId}/document-generation`,
  },
  /** Standard workspace booking APIs (`/v1/bookings`, `/v1/payments`, `/v1/receipts`). */
  bookings: {
    list: '/v1/bookings',
    get: (bookingId: string) => `/v1/bookings/${bookingId}`,
    dashboard: (bookingId: string) => `/v1/bookings/${bookingId}/dashboard`,
    timeline: (bookingId: string) => `/v1/bookings/${bookingId}/timeline`,
    updateOverview: (bookingId: string) => `/v1/bookings/${bookingId}/overview`,
    ownerWorkflow: (bookingId: string) => `/v1/bookings/${bookingId}/owner-workflow`,
    upsertOwnerWorkflow: (bookingId: string) => `/v1/bookings/${bookingId}/owner-workflow`,
    addCoOwner: (bookingId: string) => `/v1/bookings/${bookingId}/co-owners`,
    uploadDocument: (bookingId: string) => `/v1/bookings/${bookingId}/documents`,
    deleteDocument: (bookingId: string, documentId: string) =>
      `/v1/bookings/${bookingId}/documents/${documentId}`,
    downloadDocument: (bookingId: string) =>
      `/v1/bookings/${bookingId}/documents/download`,
    recordPayment: '/v1/payments',
    approvePayment: (paymentId: string) => `/v1/payments/${paymentId}/approve`,
    generateReceipt: (paymentId: string) => `/v1/payments/${paymentId}/receipt`,
    getReceipt: (receiptId: string) => `/v1/receipts/${receiptId}`,
    downloadReceipt: (receiptId: string) =>
      `/v1/receipts/${receiptId}/download?receiptType=booking_payment_receipt`,
    confirm: (bookingId: string) => `/v1/bookings/${bookingId}/confirm`,
    checkOffers: (bookingId: string) => `/v1/bookings/${bookingId}/check-offers`,
    applyOffer: (bookingId: string) => `/v1/bookings/${bookingId}/apply-offer`,
    customerJourneyLoan: {
      lifecycle: (bookingId: string) => `/v1/bookings/${bookingId}/customer-journey-loan`,
      updateQueryItems: (bookingId: string) =>
        `/v1/bookings/${bookingId}/customer-journey-loan/query-items`,
      updateLoanDetails: (bookingId: string) =>
        `/v1/bookings/${bookingId}/customer-journey-loan/loan-details`,
      acceptSanction: (bookingId: string) =>
        `/v1/bookings/${bookingId}/customer-journey-loan/accept-sanction`,
      withdraw: (bookingId: string) =>
        `/v1/bookings/${bookingId}/customer-journey-loan/withdraw`,
    },
  },
  registrations: {
    shareAgreement: (bookingId: string) => `/v1/registrations/${bookingId}/agreement/share`,
    approveAgreement: (bookingId: string) => `/v1/customer/bookings/${bookingId}/registration/agreement/approve`,
    reviseAgreement: (bookingId: string) => `/v1/customer/bookings/${bookingId}/registration/agreement/revise`,
    paymentSummary: (bookingId: string) => `/v1/registrations/${bookingId}/payment-summary`,
    manualPayment: (bookingId: string) => `/v1/registrations/${bookingId}/payments/manual`,
    listPayments: (bookingId: string) => `/v1/registrations/${bookingId}/payments`,
    downloadReceipt: (bookingId: string, paymentId: string) =>
      `/v1/registrations/${bookingId}/payments/${paymentId}/receipt/download`,
    visitSchedule: (bookingId: string) => `/v1/registrations/${bookingId}/visit-schedule`,
    rescheduleVisitSchedule: (bookingId: string, visitId: string) => `/v1/customer/bookings/${bookingId}/registration/visits/${visitId}/reschedule`,
    visitScheduleReminder: (bookingId: string) => `/v1/registrations/${bookingId}/visit-schedule/reminder`,
    uploadFinalAgreement: (bookingId: string) =>
      `/v1/registrations/${bookingId}/final-agreement`,
    schedule: (bookingId: string) => `/v1/registrations/${bookingId}/schedule`,
    uploadExecuted: (bookingId: string) => `/v1/registrations/${bookingId}/documents`,
    complete: (bookingId: string) => `/v1/registrations/${bookingId}/complete`,
  },
  loanApplications: {
    list: '/v1/loan-applications',
    get: (id: string) => `/v1/loan-applications/${id}`,
    coApplicants: {
      list: (id: string) => `/v1/loan-applications/${id}/co-applicants`,
      create: (id: string) => `/v1/loan-applications/${id}/co-applicants`,
      update: (id: string, coApplicantId: string) =>
        `/v1/loan-applications/${id}/co-applicants/${coApplicantId}`,
      delete: (id: string, coApplicantId: string) =>
        `/v1/loan-applications/${id}/co-applicants/${coApplicantId}`,
    },
  },
  location: {
    countries: '/v1/location/countries',
    states: '/v1/location/states',
    cities: '/v1/location/cities',
  },
  pincodeMaster: {
    list: '/v1/pincode-master',
    byId: (id: string) => `/v1/pincode-master/${id}`,
  },
  generics: {
    list: '/v1/generics',
    bySlug: (slug: string) => `/v1/generics/by-slug/${encodeURIComponent(slug)}`,
    namePrefixSlug: 'name-prefix',
  },
  integrations: {
    idp: {
      parse: '/v1/integrations/idp/parse',
    },
    surepass: {
      verify: '/v1/integrations/surepass/verify',
      verifyAadhaarMask: '/v1/integrations/surepass/verify/aadhaar-mask',
    },
  },
  /** Online payment gateway (Razorpay). */
  paymentGateway: {
    initiate: '/v1/payments/gateway/initiate',
    confirm: '/v1/payments/gateway/confirm',
    cancel: '/v1/payments/gateway/cancel',
  },
  /** Push notifications, device tokens, and preferences. */
  notifications: {
    list: '/v1/push-notifications',
    unreadCount: '/v1/push-notifications/unread-count',
    markRead: (id: string) =>
      `/v1/push-notifications/${encodeURIComponent(id)}/read`,
    markAllRead: '/v1/push-notifications/mark-all-read',
    deviceToken: {
      register: '/v1/device-tokens',
      deactivate: '/v1/device-tokens',
    },
    preferences: {
      get: '/v1/notification-preferences',
      update: '/v1/notification-preferences',
    },
  },
};
